import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { zonedTimeToUtc } from "date-fns-tz";

import {
  IComposerActions,
  useComposerState,
  useComposerActions
} from "contextApi/composerContext";
import { AgentChangeArea, IChannelAttachments, IComposerState } from "contextApi/composerContext/reducer";
import contentTypes from "components/common/contentTypes/data/content-types";
import { callApi } from "utils/ContentoApi";
import { callComposerAgentApi, callComposerAgentStream, waitForCreatedVisual } from "services/composerAgent";
import {
  fetchImageSuggestions,
  downloadUnsplashImage,
  uploadImageByUrl
} from "services/post";
import { resolveBestTime } from "../../postComposer/scheduler/bestTime";
import {
  AgentImageRefPart,
  AgentToolCallPart,
  AgentToolResultPart,
  CAPTION_SERVICES,
  ComposerSnapshot,
  MentionRef,
  SnapshotVisual,
  TaggedProfile,
  buildSnapshot,
  formatAspectRatio,
  formatLocalDateTime,
  linkedinSlugVariants,
  matchesMentionQuery,
  mentionTextFor,
  rankMentionCandidates,
  reactNodeToText,
  serviceLabel,
  truthyEntries
} from "./protocol";
import { VisualSlide, extractVisualTexts, formatVisualTexts, setVisualText as replaceVisualText } from "./visualText";
import { checkExistingMedia, MediaChoice } from "./existingMedia";
import { collectMentions, isSameMention, mentionsForReplacedCaption } from "./mentions";
import { getPostKey } from "./sessionLog";

// Composer actions dispatch into React state, so a tool that follows another must wait for the commit.
const RENDER_SETTLE_MS = 100;
// The visual editor measures overflow after its iframe reloads and fonts settle; this bounds the wait.
const VISUAL_OVERFLOW_WAIT_MS = 2500;
const MAX_UNDO_ENTRIES = 20;
const VISUAL_REVIEW_NOTE =
  "Review the rendering before replying: every text readable against its background, the logo clearly visible and undistorted, " +
  "nothing cut off or overlapping, and the requested change visible. Fix any problem with another edit_visual first. " +
  "Keep the review to yourself: the reply states the outcome in a sentence or two and does not describe the pictures or the comparison.";
const VISUAL_COMPARE_NOTE =
  "The pictures show the visual before the edit and after it, in that order, one per slide, followed by zoomed crops of the region that changed. " +
  "The pixel comparison above is measured: no changed pixels means the edit had no effect, so say so instead of claiming a change and retry with instructions that name the element and the exact CSS property and value.";
const CAPTION_SETTLE_ATTEMPTS = 10;
const MAX_IMAGE_CANDIDATES = 6;
const MAX_MENTION_CANDIDATES = 6;
const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CATEGORY_OPTIONS: { id: string; title: string }[] = Object.values(
  contentTypes as Record<string, { id: string; title: string }>
).map(({ id, title }) => ({ id, title }));
// The part of the post each tool changes, so the composer can show where Create AI just worked.
const TOOL_CHANGE_AREAS: Record<string, AgentChangeArea> = {
  create_visual: "visual",
  write_caption: "caption",
  set_caption: "caption",
  set_split_mode: "caption",
  set_channels: "channels",
  set_schedule: "schedule",
  set_category: "category",
  set_profile: "profile",
  attach_image: "attachments",
  generate_image: "attachments",
  fit_image: "attachments",
  restore_image: "attachments",
  attach_uploaded_image: "attachments",
  remove_attachments: "attachments",
  set_visual_text: "visual",
  edit_visual: "visual"
};

interface LatestState {
  account: any;
  composerState: IComposerState;
  actions: IComposerActions;
}

interface UndoEntry {
  toolCallId: string;
  label: string; // what the chat showed for the change, such as "Updated the caption"
  area: AgentChangeArea;
  restore: () => Promise<void>;
  isStillApplied?: () => boolean; // false once that part of the post changed by other means, usually the user's hand
}

interface VisualRender {
  html: string;
  images: AgentImageRefPart[];
}

interface ToolContext {
  latest: () => LatestState;
  callId: string;
  undoStack: UndoEntry[]; // oldest first; undo_change pops the newest
  visualRenders: Map<string, VisualRender>; // keyed by html, the last rendering of each visual version this panel made
  imageCandidates: Map<string, any>;
  uploadedImages: Map<string, AgentImageRefPart>;
  assistantCaptionTexts: Set<string>;
  replacedPhotos: Map<string, PhotoAttachment>; // keyed by the url fit_image produced, holds the version it replaced
  mentionCandidates: Map<string, MentionCandidate>; // keyed by the key search_mentions handed the model
  assistantTagKeys: Set<string>; // service:id of every tag the assistant wrote in this conversation
  closeSignal?: AbortSignal; // aborts when the composer closes, ending the slow calls' streams so the server stops them
}

interface MentionCandidate extends MentionRef {
  serviceType?: string;
  textWithoutEmoji?: string;
  image?: string;
  description?: string;
  isSearchTerm?: boolean; // the searched text offered back by X or Instagram, not an account they returned
  [key: string]: unknown;
}

type PhotoAttachment = IComposerState["attachments"]["all"]["photoAttachments"]["attachments"][number];

interface ReadUrlResult {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  text: string;
  truncated: boolean;
}

interface ToolOutcome {
  output: string;
  isError?: boolean;
  images?: AgentImageRefPart[];
  tagged?: TaggedProfile[];
  changedArea?: AgentChangeArea; // for tools whose area depends on the call, such as undo_change
}

interface ComposerVisual {
  html: string;
  preview: string | null;
  previewIsCurrent: boolean; // the saved preview still shows this html
  clippedSlides: number[] | null;
}

interface RenderComparison {
  changedFraction: number;
  region: { x: number; y: number; width: number; height: number } | null;
  position: string | null;
  crops: { name: string; url: string; width: number; height: number }[];
}

interface RenderedVisualSlide {
  index: number;
  url: string;
  width: number;
  height: number;
}

interface GeneratedImage {
  url: string;
  width: number;
  height: number;
}

interface FittedImage extends GeneratedImage {
  mimeType: string;
  changed: boolean;
}

interface CharLimitError {
  characterCount: number;
  characterLimit: number;
  service: string;
}

interface TagLimitError {
  tagCount: number;
  tagLimit: number;
}

// The reducer types validationErrors as an array, but the caption editor stores this object in it.
interface CaptionValidationErrors {
  charLimit?: CharLimitError | false;
  tagLimit?: TagLimitError | false;
}

export interface ComposerView {
  visibleCaption: string;
  inSplitMode: boolean;
}

type ToolHandler = (
  context: ToolContext,
  input: Record<string, any>
) => Promise<ToolOutcome>;

type AttachmentsState = Pick<
  IChannelAttachments,
  "type" | "options" | "photoAttachments" | "videoAttachments" | "pdfAttachments" | "articleAttachments" | "html" | "htmlMetaData"
>;

interface CaptionState {
  inSplitMode: boolean;
  captions: Record<string, string>;
  mentions: unknown[];
  visibleCaption: string;
}

interface ScheduleState {
  optionId: IComposerState["scheduler"]["optionId"];
  pickedDate: Date | null;
  wasScheduleChanged: boolean;
}

const ok = (output: string): ToolOutcome => ({ output });
const fail = (output: string): ToolOutcome => ({ output, isError: true });
const settle = () =>
  new Promise(resolve => setTimeout(resolve, RENDER_SETTLE_MS));

const countAttachments = (
  channelAttachments: IComposerState["attachments"]["all"]
): number => {
  switch (channelAttachments.type) {
    case "photo":
      return channelAttachments.photoAttachments.attachments.length;
    case "video":
      return channelAttachments.videoAttachments.attachments.length;
    case "pdf":
      return channelAttachments.pdfAttachments.attachments.length;
    case "article":
      return channelAttachments.articleAttachments.attachment ? 1 : 0;
    case "html":
      return channelAttachments.html ? 1 : 0;
    default:
      return 0;
  }
};

const getVisual = (channelAttachments: IComposerState["attachments"]["all"]): ComposerVisual | null =>
  channelAttachments.type === "html" && channelAttachments.html
    ? {
        html: channelAttachments.html,
        preview: channelAttachments.htmlMetaData?.preview ?? null,
        previewIsCurrent: !channelAttachments.htmlEdited,
        clippedSlides: channelAttachments.htmlClippedSlides ?? null
      }
    : null;

// Parsing the visual costs a DOM parse, and the snapshot is rebuilt on every render.
let visualSlidesCache: { html: string; slides: VisualSlide[] } | null = null;
const getVisualSlides = (html: string): VisualSlide[] => {
  if (visualSlidesCache?.html !== html) visualSlidesCache = { html, slides: extractVisualTexts(html) };
  return visualSlidesCache.slides;
};

const visualSnapshotOf = (channelAttachments: IComposerState["attachments"]["all"]): SnapshotVisual | null => {
  const visual = getVisual(channelAttachments);
  return visual ? { slides: getVisualSlides(visual.html), clippedSlides: visual.clippedSlides, previewAvailable: !!visual.preview } : null;
};

const existingMediaOf = (channelAttachments: IComposerState["attachments"]["all"]) => ({
  type: channelAttachments.type ?? null,
  count: countAttachments(channelAttachments)
});

const clearAttachments = (actions: IComposerActions, channelAttachments: IComposerState["attachments"]["all"]) => {
  if (channelAttachments.type === "article") {
    actions.removeArticleAttachment();
    return;
  }
  if (!channelAttachments.type) return;
  for (let index = countAttachments(channelAttachments) - 1; index >= 0; index--) {
    actions.removeAttachment(index, channelAttachments.type);
  }
};

// Called once the new image is ready, so a failed upload or generation leaves the post as it was.
const placePhoto = async (context: ToolContext, { existingMedia, photoIndex }: MediaChoice, photo: { path: string; metaData: Record<string, unknown> }) => {
  const { actions, composerState } = context.latest();
  const channelAttachments = composerState.attachments.all;
  if (existingMedia === "replace" && typeof photoIndex === "number") {
    replacePhoto(actions, photoIndex, countAttachments(channelAttachments), photo);
    return;
  }
  if (existingMedia === "replace") {
    clearAttachments(actions, channelAttachments);
    await settle();
  }
  addPhoto(context.latest().actions, photo);
};

const waitForVisualOverflow = async (context: ToolContext): Promise<number[] | null> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < VISUAL_OVERFLOW_WAIT_MS) {
    await settle();
    const clipped = context.latest().composerState.attachments.all.htmlClippedSlides;
    if (clipped) return clipped;
  }
  return null;
};

const stopIfClosed = (closeSignal?: AbortSignal) => {
  if (closeSignal?.aborted) throw new Error("The composer closed");
};

// Checked when an action is called rather than in latest(): tools keep actions across their awaits.
const guardActions = (actions: IComposerActions, closeSignal?: AbortSignal): IComposerActions =>
  new Proxy(actions, {
    get: (target, name, receiver) => {
      const value = Reflect.get(target, name, receiver);
      if (typeof value !== "function") return value;
      return (...args: unknown[]) => {
        stopIfClosed(closeSignal);
        return value(...args);
      };
    }
  });

const renderVisual = async (context: ToolContext, html: string, label = ""): Promise<AgentImageRefPart[]> => {
  const known = context.visualRenders.get(html);
  if (known) return known.images.map(image => ({ ...image, name: `${label}${image.name}` }));
  // Renders and comparisons don't take the close signal, so without this check they keep the server busy for a closed post.
  stopIfClosed(context.closeSignal);
  const { account } = context.latest();
  const rendered = await callComposerAgentApi<{ slides: RenderedVisualSlide[] }>({
    method: "post",
    url: `/accounts/${account.id}/composer-agent/visual/render`,
    data: { html }
  });
  const images = rendered.slides.map(slide => ({
    type: "image_ref" as const,
    url: slide.url,
    mimeType: "image/png",
    name: `slide ${slide.index + 1}`,
    width: slide.width,
    height: slide.height
  }));
  context.visualRenders.set(html, { html, images });
  return images.map(image => ({ ...image, name: `${label}${image.name}` }));
};

// The saved preview is a free "before" picture as long as the html is what was loaded.
const seedVisualRender = (context: ToolContext, visual: ComposerVisual) => {
  if (visual.preview && visual.previewIsCurrent && !context.visualRenders.has(visual.html)) {
    context.visualRenders.set(visual.html, {
      html: visual.html,
      images: [{ type: "image_ref", url: visual.preview, mimeType: "image/png", name: "slide 1" }]
    });
  }
};

const describeComparison = (comparison: RenderComparison, slide: number): string => {
  if (!comparison.region) return `Slide ${slide}: the rendering did not change at all; the edit had no visible effect.`;
  const percent = (comparison.changedFraction * 100).toFixed(comparison.changedFraction < 0.01 ? 2 : 1);
  const { x, y, width, height } = comparison.region;
  return `Slide ${slide}: ${percent}% of the pixels changed, within ${width}x${height} px at x=${x}, y=${y} (${comparison.position}); zoomed crops of that region are attached.`;
};

const compareVisualRenders = async (context: ToolContext, before: AgentImageRefPart[], after: AgentImageRefPart[]) => {
  stopIfClosed(context.closeSignal);
  const { account } = context.latest();
  const pairs = before.slice(0, after.length).map((image, index) => ({ before: image, after: after[index], slide: index + 1 }));
  const comparisons = await Promise.all(
    pairs.map(async pair => {
      const comparison = await callComposerAgentApi<RenderComparison>({
        method: "post",
        url: `/accounts/${account.id}/composer-agent/visual/compare`,
        data: { beforeUrl: pair.before.url, afterUrl: pair.after.url }
      });
      return { slide: pair.slide, comparison };
    })
  );
  return {
    text: comparisons.map(({ slide, comparison }) => describeComparison(comparison, slide)).join("\n"),
    crops: comparisons.flatMap(({ slide, comparison }) =>
      comparison.crops.map(crop => ({
        type: "image_ref" as const,
        url: crop.url,
        mimeType: "image/png",
        name: `${crop.name} zoom, slide ${slide}`,
        width: crop.width,
        height: crop.height
      }))
    )
  };
};

const applyVisualHtml = async (context: ToolContext, html: string): Promise<string> => {
  const { actions } = context.latest();
  actions.setVisualOverflow(null);
  actions.updateAttachmentHtml(html);
  const clippedSlides = await waitForVisualOverflow(context);
  return formatVisualTexts(getVisualSlides(html), clippedSlides);
};

// ADD_ATTACHMENTS appends to the photo list without switching the attachment type, so the composer would show nothing.
const addPhoto = (actions: IComposerActions, photo: { path: string; metaData: Record<string, unknown> }) => {
  actions.setAttachmentType("photo");
  actions.addUploadedAttachments([photo], "photo");
};

const getPhotoAttachments = (channelAttachments: IComposerState["attachments"]["all"]): PhotoAttachment[] =>
  channelAttachments.type === "photo" ? channelAttachments.photoAttachments.attachments : [];

const replacePhoto = (actions: IComposerActions, index: number, count: number, photo: { path: string; metaData: Record<string, unknown> }) => {
  actions.removeAttachment(index, "photo");
  addPhoto(actions, photo);
  actions.reorderAttachment(count - 1, index, "photo");
};

const describeAttachmentIssues = (channelAttachments: IComposerState["attachments"]["all"]): string => {
  const issues = (channelAttachments.validationErrors ?? []).filter(issue => issue?.type !== "info");
  if (issues.length === 0) return "";
  const descriptions = issues.map(issue => `[${issue.service ?? "all"}] ${reactNodeToText(issue.message).replace(/\s+/g, " ").trim()}`);
  return ` Remaining attachment issues: ${descriptions.join(" | ")}`;
};

// Attachment validation runs in an effect after the new attachments render, one settle later.
const waitForAttachmentValidation = async () => {
  await settle();
  await settle();
};

const getEnabledServices = ({ composerState, actions }: LatestState) =>
  actions.getEnabledServices(composerState.postData.channels ?? []);

const recordUndo = (context: ToolContext, entry: Omit<UndoEntry, "toolCallId">) => {
  context.undoStack.push({ ...entry, toolCallId: context.callId });
  if (context.undoStack.length > MAX_UNDO_ENTRIES) context.undoStack.shift();
};

const captureCaptions = ({ composerState }: LatestState): CaptionState => ({
  inSplitMode: composerState.inSplitMode,
  captions: { ...(composerState.postData.caption ?? {}) },
  mentions: [...((composerState.postData.mentions ?? []) as unknown[])],
  visibleCaption: composerState.visibleCaption
});

const captionMatches = (state: LatestState, target: string, text: string): boolean => {
  const captions = state.composerState.postData.caption ?? {};
  if (target === "all" && state.composerState.inSplitMode) {
    return getEnabledServices(state).every(service => captions[service] === text);
  }
  return captions[target] === text;
};

const restoreCaptions = async (context: ToolContext, before: CaptionState) => {
  if (context.latest().composerState.inSplitMode !== before.inSplitMode) {
    context.latest().actions.splitEditor();
    await settle();
  }
  const { actions } = context.latest();
  if (before.inSplitMode) {
    for (const service of getEnabledServices(context.latest())) {
      const text = before.captions[service] ?? "";
      actions.setVisibleCaption(service);
      await settle();
      actions.setChosenSuggestedCaption({ [service]: text });
      await waitForCaption(context, service, text);
    }
  } else {
    const text = before.captions.all ?? "";
    actions.setChosenSuggestedCaption({ all: text });
    await waitForCaption(context, "all", text);
  }
  actions.setPostData({ mentions: before.mentions });
  actions.setVisibleCaption(before.visibleCaption);
  await settle();
};

const captureAttachments = (channelAttachments: IChannelAttachments): AttachmentsState => ({
  type: channelAttachments.type,
  options: channelAttachments.options,
  photoAttachments: channelAttachments.photoAttachments,
  videoAttachments: channelAttachments.videoAttachments,
  pdfAttachments: channelAttachments.pdfAttachments,
  articleAttachments: channelAttachments.articleAttachments,
  html: channelAttachments.html,
  htmlMetaData: channelAttachments.htmlMetaData
});

const attachmentsSignature = (channelAttachments: AttachmentsState): string =>
  JSON.stringify({
    type: channelAttachments.type,
    photos: channelAttachments.photoAttachments.attachments.map(photo => photo.url),
    videos: channelAttachments.videoAttachments.attachments.map(video => video.url),
    pdfs: channelAttachments.pdfAttachments.attachments.map(pdf => pdf.url),
    article: channelAttachments.articleAttachments.attachment?.url ?? null,
    html: channelAttachments.html ?? null
  });

const restoreAttachments = async (context: ToolContext, before: AttachmentsState) => {
  const { actions } = context.latest();
  actions.setVisualOverflow(null);
  actions.setAttachments(before);
  await waitForAttachmentValidation();
};

// Records an attachment change once it has settled, so undo can also tell whether the user touched it since.
const recordAttachmentUndo = (context: ToolContext, before: AttachmentsState, label: string) => {
  const after = attachmentsSignature(captureAttachments(context.latest().composerState.attachments.all));
  recordUndo(context, {
    label,
    area: "attachments",
    restore: () => restoreAttachments(context, before),
    isStillApplied: () => attachmentsSignature(captureAttachments(context.latest().composerState.attachments.all)) === after
  });
};

const captureSchedule = ({ composerState }: LatestState): ScheduleState => ({
  optionId: composerState.scheduler.optionId,
  pickedDate: composerState.scheduler.pickedDate,
  wasScheduleChanged: composerState.scheduler.wasScheduleChanged
});

const sameSchedule = (a: ScheduleState, b: ScheduleState): boolean =>
  a.optionId === b.optionId && (a.pickedDate?.getTime() ?? null) === (b.pickedDate?.getTime() ?? null);

const recordScheduleUndo = (context: ToolContext, before: ScheduleState, label: string) => {
  const after = captureSchedule(context.latest());
  recordUndo(context, {
    label,
    area: "schedule",
    restore: async () => {
      const { actions } = context.latest();
      actions.setPickedDate(before.pickedDate);
      actions.setSchedulerOptionId(before.optionId);
      actions.setWasScheduleChanged(before.wasScheduleChanged);
      await settle();
    },
    isStillApplied: () => sameSchedule(captureSchedule(context.latest()), after)
  });
};

const tagKey = (mention: MentionRef) => `${mention.service}:${mention.id}`;

const trimmedOrNull = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);

const toTaggedProfile = (candidate: MentionCandidate): TaggedProfile => ({
  service: candidate.service,
  name: candidate.text,
  image: trimmedOrNull(candidate.image),
  description: trimmedOrNull(candidate.description),
  kind: candidate.serviceType ?? null
});

const describeNewTags = (postData: IComposerState["postData"], chosen: MentionRef[]): string => {
  if (chosen.length === 0) return "";
  const landed = collectMentions(postData).filter(mention => chosen.some(candidate => isSameMention(mention, candidate)));
  if (landed.length === 0) return " The tags did not land in the editor; tell the user.";
  return ` Added the tag${landed.length === 1 ? "" : "s"} ${landed.map(mentionTextFor).join(", ")}.`;
};

const waitForCaption = async (context: ToolContext, service: string, text: string) => {
  for (let attempt = 0; attempt < CAPTION_SETTLE_ATTEMPTS; attempt++) {
    await settle();
    if (context.latest().composerState.postData.caption?.[service] === text) break;
  }
  await settle();
};

// Splitting seeds each service from the shared editor state, which can lag behind the
// caption text; re-apply the shared text wherever the split left a service empty.
const fillServiceCaptions = async (
  context: ToolContext,
  services: string[],
  sharedText: string
) => {
  if (!sharedText) return;
  for (const service of services) {
    const { composerState, actions } = context.latest();
    if (composerState.postData.caption?.[service] === sharedText) continue;
    actions.setVisibleCaption(service);
    await settle();
    actions.setChosenSuggestedCaption({ [service]: sharedText });
    await waitForCaption(context, service, sharedText);
  }
};

const describeCaptionValidation = (state: LatestState, target: string): string => {
  const { charLimit, tagLimit } = state.composerState
    .validationErrors as unknown as CaptionValidationErrors;
  if (charLimit) {
    const advice =
      target === "all"
        ? ` Write a shorter caption with target ${charLimit.service}; the other services keep this one.`
        : " Shorten it.";
    return ` Over the ${serviceLabel(charLimit.service)} limit: ${charLimit.characterCount} of ${charLimit.characterLimit} characters.${advice}`;
  }
  if (tagLimit) {
    return ` Too many hashtags: ${tagLimit.tagCount} of ${tagLimit.tagLimit}.`;
  }
  return "";
};

const describeValidation = (state: LatestState): string => {
  const errors = truthyEntries(
    state.composerState.validationErrors as unknown as Record<string, unknown>
  );
  return Object.keys(errors).length
    ? ` Validation errors: ${JSON.stringify(errors)}.`
    : "";
};

// verb names the change in the result and the undo label: "Updated" for the assistant's own text, "Wrote" for Create's writer.
const applyCaption = async (context: ToolContext, { target, text, mentions }: Record<string, unknown>, verb: string): Promise<ToolOutcome> => {
  if (typeof target !== "string" || typeof text !== "string") {
    return fail("target and text are required");
  }
  const state = context.latest();
  const { composerState, actions } = state;
  const enabledServices = getEnabledServices(state);
  if (enabledServices.length === 0) {
    return fail("No channel is selected. Select channels before writing a caption.");
  }
  const before = captureCaptions(state);

  const mentionKeys: string[] = Array.isArray(mentions) ? mentions : [];
  let chosen: MentionCandidate[] = [];
  if (mentionKeys.length > 0) {
    if (target === "all") return fail("Tags belong to one service. Write the caption with that service as target to tag.");
    const unknown = mentionKeys.filter(key => !context.mentionCandidates.has(key));
    if (unknown.length) return fail(`Unknown mention keys: ${unknown.join(", ")}. Use keys returned by search_mentions.`);
    chosen = mentionKeys.map(key => context.mentionCandidates.get(key) as MentionCandidate);
    const foreign = chosen.filter(candidate => candidate.service !== target);
    if (foreign.length) return fail(`${foreign.map(mentionTextFor).join(", ")} can only be tagged on ${serviceLabel(foreign[0].service)}.`);
    const missing = chosen.map(mentionTextFor).filter(mentionText => !text.includes(mentionText));
    if (missing.length) return fail(`The text must contain the tag text exactly: ${missing.join(", ")}.`);
    chosen.forEach(candidate => context.assistantTagKeys.add(tagKey(candidate)));
  }
  actions.setPostData({ mentions: mentionsForReplacedCaption(composerState.postData, chosen) });

  if (target === "all" && composerState.inSplitMode) {
    for (const service of enabledServices) {
      actions.setVisibleCaption(service);
      await settle();
      actions.setChosenSuggestedCaption({ [service]: text });
      await settle();
    }
  } else if (target === "all") {
    actions.setChosenSuggestedCaption({ all: text });
  } else {
    if (!enabledServices.includes(target)) {
      return fail(
        `No selected channel uses ${target}. Selected services: ${enabledServices.join(", ")}.`
      );
    }
    if (!composerState.inSplitMode) {
      const sharedText = composerState.postData.caption?.all ?? "";
      actions.splitEditor();
      await settle();
      await fillServiceCaptions(
        context,
        enabledServices.filter(service => service !== target),
        sharedText
      );
    }
    actions.setVisibleCaption(target);
    await settle();
    actions.setChosenSuggestedCaption({ [target]: text });
  }
  const settledService =
    target === "all" && composerState.inSplitMode
      ? enabledServices[enabledServices.length - 1]
      : target;
  await waitForCaption(context, settledService, text);
  context.assistantCaptionTexts.add(text);

  const described =
    target !== "all"
      ? `the ${serviceLabel(target)} caption`
      : composerState.inSplitMode
      ? `the captions of ${enabledServices.map(serviceLabel).join(", ")}`
      : "the shared caption";
  recordUndo(context, {
    label: `${verb} ${described}`,
    area: "caption",
    restore: () => restoreCaptions(context, before),
    isStillApplied: () => captionMatches(context.latest(), target, text)
  });
  const { postData } = context.latest().composerState;
  const landedTags = collectMentions(postData);
  const tagged = chosen.filter(candidate => landedTags.some(mention => isSameMention(mention, candidate))).map(toTaggedProfile);
  return {
    ...ok(`${verb} ${described} (${text.length} characters).${describeCaptionValidation(context.latest(), target)}${describeNewTags(postData, chosen)}`),
    ...(tagged.length ? { tagged } : {})
  };
};

const setCaption: ToolHandler = (context, input) => applyCaption(context, input, "Updated");

const writeCaption: ToolHandler = async (context, { topic, language, sourceContent, category, scheduledDate, previousAttempt }) => {
  if (typeof topic !== "string" || !topic.trim()) return fail("topic is required");
  if (typeof language !== "string" || !language.trim()) return fail("language is required");
  const state = context.latest();
  if (getEnabledServices(state).length === 0) {
    return fail("No channel is selected. Select channels before writing a caption.");
  }
  const written = await callComposerAgentStream<{ text: string }>(`/accounts/${state.account.id}/composer-agent/caption/write`, {
    topic,
    language,
    sourceContent,
    category,
    scheduledDate,
    previousAttempt,
    postKey: getPostKey(state.composerState.postData) ?? undefined,
    profileId: state.composerState.agentChat.profileId ?? undefined
  }, context.closeSignal);
  return applyCaption(context, { target: "all", text: written.text }, "Wrote");
};

const fetchMentionSuggestions = async (accountId: string, service: string, term: string): Promise<MentionCandidate[]> => {
  const suggestions: MentionCandidate[] = await callApi({
    method: "get",
    url: `/accounts/${accountId}/mentions/${service}`,
    params: { "search-term": term }
  });
  // Display names can carry stray whitespace, and the editor matches the tag text exactly.
  return (Array.isArray(suggestions) ? suggestions : []).map(suggestion => ({ ...suggestion, service, text: String(suggestion.text ?? "").trim() }));
};

const mergeCandidates = (known: MentionCandidate[], more: MentionCandidate[]): MentionCandidate[] => [
  ...known,
  ...more.filter(candidate => !known.some(existing => existing.id === candidate.id))
];

const searchMentions: ToolHandler = async (context, { service, query }) => {
  if (typeof service !== "string" || !CAPTION_SERVICES.includes(service)) return fail(`service must be one of ${CAPTION_SERVICES.join(", ")}`);
  if (typeof query !== "string" || !query.trim()) return fail("query is required");
  const { account } = context.latest();
  const hasMatch = (list: MentionCandidate[]) => list.some(candidate => matchesMentionQuery(candidate, query));
  let results = await fetchMentionSuggestions(account.id, service, query.trim());
  if (service === "linkedin" && !hasMatch(results)) {
    for (const slug of linkedinSlugVariants(query)) {
      results = mergeCandidates(results, await fetchMentionSuggestions(account.id, service, slug));
      if (hasMatch(results)) break;
    }
  }
  const found = rankMentionCandidates(results, query).slice(0, MAX_MENTION_CANDIDATES);
  if (found.length === 0) return ok(`No ${serviceLabel(service)} match for "${query}".`);
  const firstIndex = context.mentionCandidates.size + 1;
  const candidates = found.map((suggestion, index) => {
    const key = `${service}-${firstIndex + index}`;
    context.mentionCandidates.set(key, suggestion);
    return {
      key,
      name: suggestion.text,
      kind: suggestion.serviceType ?? null,
      description: trimmedOrNull(suggestion.description),
      mentionText: mentionTextFor(suggestion),
      ...(suggestion.isSearchTerm ? { unconfirmed: true } : {})
    };
  });
  return ok(JSON.stringify(candidates));
};

const setSplitMode: ToolHandler = async (context, { enabled }) => {
  const state = context.latest();
  const { composerState, actions } = state;
  if (typeof enabled !== "boolean") return fail("enabled must be a boolean");
  if (enabled === composerState.inSplitMode) {
    return ok(`Split captions were already ${enabled ? "enabled" : "disabled"}.`);
  }
  if (enabled && getEnabledServices(state).length === 0) {
    return fail("Select at least one channel before splitting captions.");
  }
  const before = captureCaptions(state);
  actions.splitEditor();
  await settle();
  recordUndo(context, {
    label: enabled ? "Split the captions per channel" : "Merged the captions",
    area: "caption",
    restore: () => restoreCaptions(context, before),
    isStillApplied: () => context.latest().composerState.inSplitMode === enabled
  });
  return ok(`Split captions ${enabled ? "enabled" : "disabled"}.`);
};

const setChannels: ToolHandler = async (context, { channelIds }) => {
  const { account, composerState, actions } = context.latest();
  if (!Array.isArray(channelIds)) return fail("channelIds must be an array");
  const { id, isDraft, isPostGroup } = composerState.postData;
  if (!!id && !isDraft && !isPostGroup) {
    return fail("Channels of a scheduled post cannot be changed.");
  }
  const accountChannels: any[] = account.channels ?? [];
  const unknown = channelIds.filter(
    (channelId: string) => !accountChannels.some(channel => channel.id === channelId)
  );
  if (unknown.length) return fail(`Unknown channel ids: ${unknown.join(", ")}`);

  const uniqueIds: string[] = Array.from(new Set<string>(channelIds));
  const before: string[] = [...(composerState.postData.channels ?? [])];
  actions.onChannelSelected(uniqueIds, { emptyChannels: uniqueIds.length === 0 });
  await settle();

  const names = uniqueIds.map(
    channelId => accountChannels.find(channel => channel.id === channelId).username
  );
  const sameSelection = (ids: string[]) => ids.length === uniqueIds.length && ids.every(channelId => uniqueIds.includes(channelId));
  recordUndo(context, {
    label: names.length ? `Selected ${names.join(", ")}` : "Cleared the channels",
    area: "channels",
    restore: async () => {
      context.latest().actions.onChannelSelected(before, { emptyChannels: before.length === 0 });
      await settle();
    },
    isStillApplied: () => sameSelection(context.latest().composerState.postData.channels ?? [])
  });
  return ok(
    names.length
      ? `Selected channels: ${names.join(", ")}.${describeValidation(context.latest())}`
      : "No channel is selected now."
  );
};

const parseLocalDate = (date: string): Date => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};

const setSchedule: ToolHandler = async (context, { mode, dateTime, date }) => {
  const state = context.latest();
  const { account, composerState, actions } = state;
  const timezone: string = account.timezone;
  const before = captureSchedule(state);
  const commitPickedDate = async (pickedDate: Date) => {
    actions.setPickedDate(pickedDate);
    actions.setSchedulerOptionId("set_date_time");
    actions.setWasScheduleChanged(true);
    await settle();
    recordScheduleUndo(context, before, `Scheduled for ${formatLocalDateTime(pickedDate, timezone)}`);
    return ok(
      `Scheduled for ${formatLocalDateTime(pickedDate, timezone)} (${timezone}). The user still confirms with the button in the composer.`
    );
  };

  switch (mode) {
    case "post_now":
      actions.setSchedulerOptionId("post_now");
      actions.setWasScheduleChanged(true);
      await settle();
      recordScheduleUndo(context, before, "Set to post now");
      return ok("The post goes out as soon as the user clicks the button.");
    case "auto_schedule":
      if (composerState.postData.id) {
        return fail("Auto-schedule is only available for new posts.");
      }
      actions.setSchedulerOptionId("auto_schedule");
      actions.setWasScheduleChanged(true);
      await settle();
      recordScheduleUndo(context, before, "Set to auto-schedule");
      return ok("Willow picks the next free slot when the user clicks the button.");
    case "custom": {
      if (typeof dateTime !== "string" || !LOCAL_DATE_TIME.test(dateTime)) {
        return fail("dateTime must look like YYYY-MM-DDTHH:mm");
      }
      const pickedDate = timezone
        ? zonedTimeToUtc(dateTime, timezone)
        : new Date(dateTime);
      if (Number.isNaN(pickedDate.getTime())) return fail("dateTime is not a valid date");
      if (pickedDate.getTime() <= Date.now()) return fail("dateTime must be in the future");
      return commitPickedDate(pickedDate);
    }
    case "best_time": {
      if (typeof date !== "string" || !LOCAL_DATE.test(date)) {
        return fail("date must look like YYYY-MM-DD");
      }
      const todayLocal = formatLocalDateTime(new Date(), timezone).slice(0, 10);
      if (date < todayLocal) return fail("date must be today or later");
      const pickedDate = await resolveBestTime({
        accountId: account.id,
        accountChannels: account.channels ?? [],
        selectedChannelIds: composerState.postData.channels ?? [],
        date: date === todayLocal ? new Date() : parseLocalDate(date)
      });
      return commitPickedDate(pickedDate);
    }
    default:
      return fail("mode must be custom, best_time, auto_schedule or post_now");
  }
};

const setCategory: ToolHandler = async (context, { contentTypeId }) => {
  const { actions } = context.latest();
  const option = CATEGORY_OPTIONS.find(category => category.id === contentTypeId);
  if (!option) {
    return fail(
      `Unknown category. Valid ids: ${CATEGORY_OPTIONS.map(category => category.id).join(", ")}`
    );
  }
  const { postData } = context.latest().composerState;
  const before = { contentTypeId: postData.contentTypeId ?? null, postIdea: postData.postIdea ?? null };
  actions.setPostData({ contentTypeId, postIdea: null });
  await settle();
  recordUndo(context, {
    label: `Category set to ${option.title}`,
    area: "category",
    restore: async () => {
      context.latest().actions.setPostData(before);
      await settle();
    },
    isStillApplied: () => context.latest().composerState.postData.contentTypeId === contentTypeId
  });
  return ok(`Category set to ${option.title}.`);
};

const setProfile: ToolHandler = async (context, { profileId }) => {
  const { composerState, actions } = context.latest();
  const { profiles, profileId: previousProfileId, profileChosenBy: previousChooser } = composerState.agentChat;
  if (profiles.length === 0) return fail("The profile list has not loaded yet. Keep the active profile for now.");
  const profile = profiles.find(candidate => candidate.id === profileId);
  if (!profile) return fail(`Unknown profile. Valid ids: ${profiles.map(candidate => candidate.id).join(", ")}`);
  const label = profile.name ?? "Untitled profile";
  if (profileId === previousProfileId) return ok(`Already writing as ${label}.`);
  actions.setAgentChat({ profileId, profileChosenBy: "assistant" });
  await settle();
  recordUndo(context, {
    label: `Switched to ${label}`,
    area: "profile",
    restore: async () => {
      context.latest().actions.setAgentChat({ profileId: previousProfileId, profileChosenBy: previousChooser });
      await settle();
    },
    isStillApplied: () => context.latest().composerState.agentChat.profileId === profileId
  });
  return ok(`Now writing as ${label}. Its voice, language and brand rules apply from the next step on.`);
};

const searchImages: ToolHandler = async (context, { query }) => {
  if (typeof query !== "string" || !query.trim()) return fail("query is required");
  const { actions } = context.latest();
  const images: any[] = await fetchImageSuggestions(query, false);
  const candidates = images.slice(0, MAX_IMAGE_CANDIDATES);
  candidates.forEach(image => context.imageCandidates.set(image.id, image));
  actions.setImagesSearchTerm(query);
  actions.setSuggestedImages(images);
  if (candidates.length === 0) return ok("No photos found. Try a different query.");
  return ok(
    JSON.stringify(
      candidates.map(image => ({
        id: image.id,
        description: image.alt_description ?? image.description ?? "",
        photographer: image.user?.name ?? null,
        orientation: image.width >= image.height ? "landscape" : "portrait",
        aspectRatio: formatAspectRatio(image.width, image.height)
      }))
    )
  );
};

const attachImage: ToolHandler = async (context, { imageId, existingMedia, photoIndex }) => {
  const { actions, composerState } = context.latest();
  const blocked = checkExistingMedia(existingMediaOf(composerState.attachments.all), { existingMedia, photoIndex });
  if (blocked) return fail(blocked);
  const image = context.imageCandidates.get(imageId);
  if (!image) return fail("Unknown imageId. Call search_images first and use one of its ids.");
  const before = captureAttachments(composerState.attachments.all);

  actions.setIsUploadingAttachments(true, "photo");
  try {
    downloadUnsplashImage(image.id);
    const uploadedImage = await uploadImageByUrl(image.urls.full);
    await placePhoto(context, { existingMedia, photoIndex }, { path: uploadedImage.path, metaData: { height: image.height, width: image.width } });
  } finally {
    actions.setIsUploadingAttachments(false, "photo");
  }
  await settle();
  const count = countAttachments(context.latest().composerState.attachments.all);
  const credit = image.user?.name ? ` by ${image.user.name}` : "";
  recordAttachmentUndo(context, before, "Attached an image");
  return ok(
    `Attached the photo${credit}. The post now has ${count} photo${count === 1 ? "" : "s"}.`
  );
};

const generateImage: ToolHandler = async (context, { prompt, style, aspectRatio, existingMedia, photoIndex }) => {
  if (typeof prompt !== "string" || !prompt.trim()) return fail("prompt is required");
  const { account, actions, composerState } = context.latest();
  const blocked = checkExistingMedia(existingMediaOf(composerState.attachments.all), { existingMedia, photoIndex });
  if (blocked) return fail(blocked);
  const before = captureAttachments(composerState.attachments.all);

  actions.setIsUploadingAttachments(true, "photo");
  let image: GeneratedImage;
  try {
    image = await callComposerAgentStream<GeneratedImage>(`/accounts/${account.id}/composer-agent/images`, {
      prompt,
      style,
      aspectRatio,
      profileId: composerState.agentChat.profileId ?? undefined
    }, context.closeSignal);
    await placePhoto(context, { existingMedia, photoIndex }, { path: image.url, metaData: { height: image.height, width: image.width } });
  } finally {
    actions.setIsUploadingAttachments(false, "photo");
  }
  await settle();
  const count = countAttachments(context.latest().composerState.attachments.all);
  recordAttachmentUndo(context, before, "Generated an image");
  return ok(
    `Generated and attached the image (${image.width}x${image.height}). The post now has ${count} photo${count === 1 ? "" : "s"}.`
  );
};

const fitImage: ToolHandler = async (context, { index, aspectRatio, method }) => {
  const { account, composerState, actions } = context.latest();
  const photos = getPhotoAttachments(composerState.attachments.all);
  const photo = Number.isInteger(index) ? photos[index] : undefined;
  if (!photo) {
    return fail(
      photos.length === 0 ? "The post has no image to adjust." : `No image at index ${index}. Valid indexes: 0 to ${photos.length - 1}.`
    );
  }

  const before = captureAttachments(composerState.attachments.all);
  actions.setIsUploadingAttachments(true, "photo");
  let image: FittedImage;
  try {
    image = await callComposerAgentStream<FittedImage>(`/accounts/${account.id}/composer-agent/images/fit`, { url: photo.url, aspectRatio, method }, context.closeSignal);
    if (image.changed) {
      const metaData = { ...(photo.metaData ?? {}), width: image.width, height: image.height, mime: image.mimeType, ext: image.mimeType.split("/")[1] };
      replacePhoto(actions, index, photos.length, { path: image.url, metaData });
      context.replacedPhotos.set(image.url, photo);
    }
  } finally {
    actions.setIsUploadingAttachments(false, "photo");
  }
  await waitForAttachmentValidation();
  const issues = describeAttachmentIssues(context.latest().composerState.attachments.all);
  const size = `${image.width}x${image.height}, ratio ${formatAspectRatio(image.width, image.height)}`;
  if (!image.changed) return ok(`Image ${index + 1} already has that ratio (${size}).${issues}`);
  recordAttachmentUndo(context, before, `${method === "extend" ? "Extended" : "Cropped"} image ${index + 1}`);
  return ok(`${method === "extend" ? "Extended" : "Cropped"} image ${index + 1} to ${size}.${issues}`);
};

const restoreImage: ToolHandler = async (context, { index }) => {
  const { composerState, actions } = context.latest();
  const photos = getPhotoAttachments(composerState.attachments.all);
  const photo = Number.isInteger(index) ? photos[index] : undefined;
  if (!photo) return fail(photos.length === 0 ? "The post has no image." : `No image at index ${index}. Valid indexes: 0 to ${photos.length - 1}.`);
  const previous = context.replacedPhotos.get(photo.url);
  if (!previous) return fail(`Image ${index + 1} was not changed by fit_image, so there is nothing to restore.`);

  replacePhoto(actions, index, photos.length, { path: previous.url, metaData: previous.metaData ?? {} });
  context.replacedPhotos.delete(photo.url);
  await waitForAttachmentValidation();
  const issues = describeAttachmentIssues(context.latest().composerState.attachments.all);
  const { width, height } = previous.metaData ?? {};
  return ok(`Restored image ${index + 1} to ${width}x${height}, ratio ${formatAspectRatio(width, height)}.${issues}`);
};

const readUrl: ToolHandler = async (context, { url, attachLink }) => {
  if (typeof url !== "string" || !url.trim()) return fail("url is required");
  const { account, actions } = context.latest();
  const page = await callComposerAgentStream<ReadUrlResult>(`/accounts/${account.id}/composer-agent/read-url`, { url }, context.closeSignal);
  if (attachLink !== true) return ok(JSON.stringify(page));

  const before = captureAttachments(context.latest().composerState.attachments.all);
  await actions.scrapeUrl(page.url);
  await settle();
  const attached = context.latest().composerState.attachments.all.articleAttachments.attachment;
  if (attached) recordAttachmentUndo(context, before, "Attached a link preview");
  const note = attached
    ? `Link preview attached (${attached.url}).`
    : "The link could not be attached because no preview image was found; the post goes out without a link preview.";
  return ok(JSON.stringify({ ...page, note }));
};

const webSearch: ToolHandler = async (context, { query, limit }) => {
  if (typeof query !== "string" || !query.trim()) return fail("query is required");
  const { account } = context.latest();
  const results = await callComposerAgentStream<unknown[]>(`/accounts/${account.id}/composer-agent/web-search`, { query, limit }, context.closeSignal);
  if (!Array.isArray(results) || results.length === 0) return ok("No results found.");
  return ok(JSON.stringify(results));
};

const attachUploadedImage: ToolHandler = async (context, { url, existingMedia, photoIndex }) => {
  const image = typeof url === "string" ? context.uploadedImages.get(url) : undefined;
  if (!image) {
    return fail("Unknown url. Only images the user uploaded to this chat can be attached; their urls are in the conversation.");
  }
  const { composerState } = context.latest();
  const blocked = checkExistingMedia(existingMediaOf(composerState.attachments.all), { existingMedia, photoIndex });
  if (blocked) return fail(blocked);
  const before = captureAttachments(composerState.attachments.all);
  await placePhoto(context, { existingMedia, photoIndex }, { path: image.url, metaData: { height: image.height ?? 0, width: image.width ?? 0 } });
  await settle();
  const count = countAttachments(context.latest().composerState.attachments.all);
  if (count === 0) {
    // A replace has already cleared the post, and no undo entry exists yet to bring that back.
    await restoreAttachments(context, before);
    return fail(`${image.name ?? "The image"} could not be attached, so the post was left as it was.`);
  }
  recordAttachmentUndo(context, before, "Attached your image");
  return ok(`Attached ${image.name ?? "the image"}. The post now has ${count} photo${count === 1 ? "" : "s"}.`);
};

const getRecentPosts: ToolHandler = async (context, { service, query, limit }) => {
  const { account } = context.latest();
  const posts = await callComposerAgentApi<unknown[]>({
    method: "get",
    url: `/accounts/${account.id}/composer-agent/recent-posts`,
    params: { service, query, limit }
  });
  if (!Array.isArray(posts) || posts.length === 0) return ok("No published posts found.");
  return ok(JSON.stringify(posts));
};

const recordVisualUndo = (context: ToolContext, beforeHtml: string, afterHtml: string, label: string) =>
  recordUndo(context, {
    label,
    area: "visual",
    restore: async () => {
      await applyVisualHtml(context, beforeHtml);
    },
    isStillApplied: () => context.latest().composerState.attachments.all.html === afterHtml
  });

// The visual is drawn from the caption the channels share; with split captions, the first selected service's text stands in.
const captionForVisual = (state: LatestState): string => {
  const captions = state.composerState.postData.caption ?? {};
  const service = state.composerState.inSplitMode ? getEnabledServices(state)[0] : "all";
  return (captions[service] ?? captions.all ?? "").trim();
};

const createVisual: ToolHandler = async (context, { format, slideCount, replace }) => {
  const state = context.latest();
  const { account, composerState, actions } = state;
  const channelAttachments = composerState.attachments.all;
  const text = captionForVisual(state);
  if (!text) return fail("The caption is empty. Write the caption first; the visual is drawn from it.");
  if (getVisual(channelAttachments) && replace !== true) {
    return fail("The post already has a Willow Create visual. Pass replace true to make a new one in its place, or use edit_visual to change it.");
  }
  if (countAttachments(channelAttachments) > 0 && replace !== true) {
    return fail("The post already carries attachments. Confirm with the user, then pass replace true to swap them for the visual.");
  }
  const before = captureAttachments(channelAttachments);
  const pickedDate = composerState.scheduler.pickedDate;
  const { jobId } = await callComposerAgentApi<{ jobId: string }>({
    method: "post",
    url: `/accounts/${account.id}/composer-agent/visual/create`,
    data: {
      text,
      format,
      slideCount,
      scheduledDate: pickedDate ? formatLocalDateTime(pickedDate, account.timezone).slice(0, 10) : undefined,
      postKey: getPostKey(composerState.postData) ?? undefined,
      profileId: composerState.agentChat.profileId ?? undefined
    }
  });
  const created = await waitForCreatedVisual(account.id, jobId, context.closeSignal);
  actions.setVisualOverflow(null);
  actions.setAttachments({
    html: created.html,
    htmlMetaData: { preview: created.preview.url, fileName: created.fileName },
    htmlEdited: false,
    htmlClippedSlides: undefined
  });
  actions.setAttachmentType("html");
  await settle();
  const visual = getVisual(context.latest().composerState.attachments.all);
  if (visual) seedVisualRender(context, visual);
  recordUndo(context, {
    label: format === "carousel" ? "Created a Willow Create carousel" : "Created a Willow Create visual",
    area: "visual",
    restore: () => restoreAttachments(context, before),
    isStillApplied: () => context.latest().composerState.attachments.all.html === created.html
  });
  const clippedSlides = await waitForVisualOverflow(context);
  const texts = formatVisualTexts(getVisualSlides(created.html), clippedSlides);
  try {
    const images = await renderVisual(context, created.html);
    return { output: `Created the Willow Create visual and attached it.\n${texts}\nThe rendering follows, one picture per slide. ${VISUAL_REVIEW_NOTE}`, images };
  } catch (err) {
    if (!context.closeSignal?.aborted) console.error("Failed to render the created visual", { err });
    return ok(`Created the Willow Create visual and attached it.\n${texts}\nThe result could not be rendered for review; the composer shows it live, so ask the user to check it.`);
  }
};

const viewVisual: ToolHandler = async (context, { fresh }) => {
  const visual = getVisual(context.latest().composerState.attachments.all);
  if (!visual) return fail("The post has no Willow Create visual.");
  const texts = formatVisualTexts(getVisualSlides(visual.html), visual.clippedSlides);
  seedVisualRender(context, visual);
  if (fresh === true) {
    const images = await renderVisual(context, visual.html);
    return { output: `Rendered the visual as it is now, one picture per slide.\n${texts}`, images };
  }
  const note = visual.preview
    ? "The attached picture is the preview rendered when the post was last saved; the text listed below is the current one."
    : "No saved preview is available; call again with fresh true to render the visual as it is now.";
  return {
    output: `${note}\n${texts}`,
    ...(visual.preview ? { images: [{ type: "image_ref" as const, url: visual.preview, mimeType: "image/png", name: "saved preview" }] } : {})
  };
};

const setVisualText: ToolHandler = async (context, { slide, index, text }) => {
  const visual = getVisual(context.latest().composerState.attachments.all);
  if (!visual) return fail("The post has no Willow Create visual.");
  if (!Number.isInteger(slide) || !Number.isInteger(index)) return fail("slide and index must be the integers shown in attachments.visual.");
  if (typeof text !== "string" || !text.trim()) return fail("text is required.");
  const edited = replaceVisualText(visual.html, slide, index, text);
  if (!edited) return fail(`There is no text element at [${slide}.${index}]; the addresses are listed in attachments.visual.`);
  const report = await applyVisualHtml(context, edited);
  recordVisualUndo(context, visual.html, edited, `Changed a text on slide ${slide + 1} of the visual`);
  return ok(`Changed [${slide}.${index}].\n${report}`);
};

const editVisual: ToolHandler = async (context, { instructions }) => {
  const { account, composerState } = context.latest();
  const visual = getVisual(composerState.attachments.all);
  if (!visual) return fail("The post has no Willow Create visual.");
  if (typeof instructions !== "string" || !instructions.trim()) return fail("instructions are required.");
  const edited = await callComposerAgentStream<{ html: string }>(`/accounts/${account.id}/composer-agent/visual/edit`, {
    html: visual.html,
    instructions,
    postKey: getPostKey(composerState.postData) ?? undefined,
    profileId: composerState.agentChat.profileId ?? undefined
  }, context.closeSignal);
  seedVisualRender(context, visual);
  const report = await applyVisualHtml(context, edited.html);
  recordVisualUndo(context, visual.html, edited.html, "Edited the visual");
  try {
    const beforeImages = await renderVisual(context, visual.html, "before, ");
    const afterImages = await renderVisual(context, edited.html, "after, ");
    const comparison = await compareVisualRenders(context, beforeImages, afterImages);
    return {
      output: `Edited the visual.\n${report}\nPixel comparison with the rendering before the edit:\n${comparison.text}\n${VISUAL_COMPARE_NOTE} ${VISUAL_REVIEW_NOTE}`,
      images: [...beforeImages, ...afterImages, ...comparison.crops]
    };
  } catch (err) {
    if (!context.closeSignal?.aborted) console.error("Failed to render the edited visual", { err });
    return ok(`Edited the visual.\n${report}\nThe result could not be rendered for review; the composer shows it live, so ask the user to check it.`);
  }
};

const removeAttachments: ToolHandler = async context => {
  const { composerState, actions } = context.latest();
  const channelAttachments = composerState.attachments.all;
  const count = countAttachments(channelAttachments);
  if (count === 0) return ok("There was nothing attached.");
  const before = captureAttachments(channelAttachments);

  clearAttachments(actions, channelAttachments);
  await settle();
  recordAttachmentUndo(context, before, "Removed the attachments");
  return ok(`Removed ${count} attachment${count === 1 ? "" : "s"}.`);
};

const undoChange: ToolHandler = async (context, { force }) => {
  const entry = context.undoStack[context.undoStack.length - 1];
  if (!entry) return fail("There is nothing to undo; only changes made through tools in this conversation can be undone.");
  if (entry.isStillApplied && !entry.isStillApplied() && force !== true) {
    return fail(`"${entry.label}" was changed in the composer since, probably by the user. Ask before overwriting; call again with force true to undo anyway.`);
  }
  context.undoStack.pop();
  await entry.restore();
  const remaining = context.undoStack.length;
  const next = remaining ? `${remaining} earlier change${remaining === 1 ? "" : "s"} can still be undone, the next being "${context.undoStack[remaining - 1].label}".` : "Nothing earlier can be undone.";
  return { ...ok(`Undid: ${entry.label}. ${next}`), changedArea: entry.area };
};

const HANDLERS: Record<string, ToolHandler> = {
  write_caption: writeCaption,
  set_caption: setCaption,
  search_mentions: searchMentions,
  set_split_mode: setSplitMode,
  set_channels: setChannels,
  set_schedule: setSchedule,
  set_category: setCategory,
  set_profile: setProfile,
  search_images: searchImages,
  attach_image: attachImage,
  generate_image: generateImage,
  fit_image: fitImage,
  restore_image: restoreImage,
  get_recent_posts: getRecentPosts,
  read_url: readUrl,
  web_search: webSearch,
  attach_uploaded_image: attachUploadedImage,
  remove_attachments: removeAttachments,
  create_visual: createVisual,
  view_visual: viewVisual,
  set_visual_text: setVisualText,
  edit_visual: editVisual,
  undo_change: undoChange
};

export const useComposerTools = () => {
  const account = useSelector((state: any) => state.account.data);
  const composerState = useComposerState(state => state);
  const actions = useComposerActions(actions => actions);
  const latest = useRef<LatestState>({ account, composerState, actions });
  latest.current = { account, composerState, actions };
  const imageCandidates = useRef<Map<string, any>>(new Map());
  const uploadedImages = useRef<Map<string, AgentImageRefPart>>(new Map());
  const assistantCaptionTexts = useRef<Set<string>>(new Set());
  const replacedPhotos = useRef<Map<string, PhotoAttachment>>(new Map());
  const mentionCandidates = useRef<Map<string, MentionCandidate>>(new Map());
  const assistantTagKeys = useRef<Set<string>>(new Set());
  const undoStack = useRef<UndoEntry[]>([]);
  const visualRenders = useRef<Map<string, VisualRender>>(new Map());

  const registerUploadedImage = useCallback((image: AgentImageRefPart) => {
    uploadedImages.current.set(image.url, image);
  }, []);

  const getSnapshot = useCallback((): ComposerSnapshot => {
    const state = latest.current;
    const { account, composerState } = state;
    const channelAttachments = composerState.attachments.all;
    return buildSnapshot({
      now: new Date(),
      timezone: account.timezone,
      inDraftMode: composerState.inDraftMode,
      postId: composerState.postData.id,
      isDraft: composerState.postData.isDraft,
      isPostGroup: composerState.postData.isPostGroup,
      selectedChannelIds: composerState.postData.channels ?? [],
      enabledServices: getEnabledServices(state),
      accountChannels: account.channels ?? [],
      inSplitMode: composerState.inSplitMode,
      visibleCaption: composerState.visibleCaption,
      captions: composerState.postData.caption ?? {},
      assistantCaptionTexts: Array.from(assistantCaptionTexts.current),
      mentions: collectMentions(composerState.postData).map(mention => ({
        ...mention,
        addedBy: assistantTagKeys.current.has(tagKey(mention)) ? "assistant" : "user"
      })),
      attachmentType: channelAttachments.type ?? null,
      attachmentCount: countAttachments(channelAttachments),
      attachmentImages: getPhotoAttachments(channelAttachments).map(photo => ({
        width: photo.metaData?.width,
        height: photo.metaData?.height
      })),
      attachmentIssues: channelAttachments.validationErrors ?? [],
      visual: visualSnapshotOf(channelAttachments),
      schedulerOptionId: composerState.scheduler.optionId,
      pickedDate: composerState.scheduler.pickedDate,
      contentTypeId: composerState.postData.contentTypeId ?? null,
      categoryOptions: CATEGORY_OPTIONS,
      validationErrors: composerState.validationErrors as unknown as Record<string, unknown>,
      undoableChanges: undoStack.current.map(entry => entry.label)
    });
  }, []);

  const executeToolCall = useCallback(
    async (call: AgentToolCallPart, closeSignal?: AbortSignal): Promise<AgentToolResultPart> => {
      const context: ToolContext = {
        // A tool can still be running when the composer closes, and a write after that lands in the reset composer the next post opens with.
        latest: () => ({ ...latest.current, actions: guardActions(latest.current.actions, closeSignal) }),
        callId: call.id,
        undoStack: undoStack.current,
        visualRenders: visualRenders.current,
        imageCandidates: imageCandidates.current,
        uploadedImages: uploadedImages.current,
        assistantCaptionTexts: assistantCaptionTexts.current,
        replacedPhotos: replacedPhotos.current,
        mentionCandidates: mentionCandidates.current,
        assistantTagKeys: assistantTagKeys.current,
        closeSignal
      };
      const handler = HANDLERS[call.name];
      let outcome: ToolOutcome;
      if (closeSignal?.aborted) {
        outcome = fail("The composer closed, so the tool did not run.");
      } else if (!handler) {
        outcome = fail(`Unknown tool ${call.name}`);
      } else {
        try {
          outcome = await handler(context, call.input ?? {});
          const area = outcome.changedArea ?? TOOL_CHANGE_AREAS[call.name];
          if (area && !outcome.isError && !closeSignal?.aborted) latest.current.actions.markAgentChange(area);
        } catch (err) {
          if (!closeSignal?.aborted) console.error("Create AI tool failed", { tool: call.name, err });
          outcome = fail(`${call.name} failed: ${(err as Error).message}`);
        }
      }
      return {
        type: "tool_result",
        toolCallId: call.id,
        output: outcome.output,
        ...(outcome.images ? { images: outcome.images } : {}),
        ...(outcome.tagged ? { tagged: outcome.tagged } : {}),
        isError: outcome.isError
      };
    },
    []
  );

  const restoreVisibleCaption = useCallback((view: ComposerView) => {
    const state = latest.current;
    const { composerState, actions } = state;
    const isSameMode = composerState.inSplitMode === view.inSplitMode;
    const isStillValid = view.inSplitMode
      ? getEnabledServices(state).includes(view.visibleCaption)
      : view.visibleCaption === "all";
    if (isSameMode && isStillValid && composerState.visibleCaption !== view.visibleCaption) {
      actions.setVisibleCaption(view.visibleCaption);
    }
  }, []);

  return { getSnapshot, executeToolCall, restoreVisibleCaption, registerUploadedImage };
};
